/**
 * ThankYouHero Component
 *
 * Full-width hero for the thank-you page: property logo, "Thank You!" heading,
 * subtitle, and two CTA cards (Visit Website + Call Now).
 * The Visit Website CTA supports optional query params (UTM tracking).
 */

import {
  useContentToken,
  usePropertyField,
  EditableToken,
  useSlotGroup,
  EditableSlotGroup,
  useSlotGroupOverrides,
} from '@vibesites/runtime/react';
import type { SlotGroupDefinition, ComponentContentSchema } from '@vibesites/runtime';
// @ts-ignore
import { VibesitesImage } from '@vibesites/ui-primitives';

function ensureAbsoluteUrl(url: string | undefined | null): string {
  if (!url || url === '#') return url || '#';
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed) || /^tel:/i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith('#') || trimmed.startsWith('/')) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

function buildCtaUrl(baseUrl: string | null, queryParams: string | null): string {
  const url = ensureAbsoluteUrl(baseUrl);
  if (!queryParams || queryParams.trim() === '' || queryParams.trim() === '?utm_source=') {
    return url;
  }
  const params = queryParams.trim().startsWith('?') ? queryParams.trim() : `?${queryParams.trim()}`;
  if (url.includes('?')) {
    return `${url}&${params.slice(1)}`;
  }
  return `${url}${params}`;
}

// ---------------------------------------------------------------------------
// Slot Group Definitions
// ---------------------------------------------------------------------------

const logoSlotDefinition: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_mono_white_logo',
      suggestedFields: ['property_mono_white_logo', 'property_secondary_logo', 'property_primary_logo'],
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Property Logo',
    },
  },
};

const heroImageDefinition: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Background Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Property exterior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: {
      image: 'image',
      alt: 'alt_text',
    },
  },
};

const visitWebsiteLinkDef: SlotGroupDefinition = {
  label: 'Visit Website Link',
  fields: {
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'VISIT OUR WEBSITE',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_website_url',
      suggestedFields: ['property_website_url', 'property_apply_now_url'],
    },
    query_params: {
      label: 'URL Query Parameters',
      type: 'text',
      description: 'Query string appended to the URL, e.g. ?utm_medium=ppc&utm_source=google&utm_campaign=landing-page',
      defaultValue: '?utm_source=',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const callNowLinkDef: SlotGroupDefinition = {
  label: 'Call Now Link',
  fields: {
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'CALL NOW',
    },
    phone: {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'custom_ppc_tracking_phone_number',
      suggestedFields: ['custom_ppc_tracking_phone_number', 'property_call_tracking_phone', 'property_phone'],
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ThankYouHero() {
  const slotGroupOverrides = useSlotGroupOverrides();

  const heading = useContentToken('thankyouhero.heading');
  const subtitle = useContentToken('thankyouhero.subtitle');
  const visitDescription = useContentToken('thankyouhero.visit_description');
  const callDescription = useContentToken('thankyouhero.call_description');

  const logo = useSlotGroup('thankyouhero.logo', logoSlotDefinition);
  const heroImage = useSlotGroup('thankyouhero.background', heroImageDefinition);
  const visitLink = useSlotGroup('thankyouhero.visit_website_link', visitWebsiteLinkDef);
  const callLink = useSlotGroup('thankyouhero.call_now_link', callNowLinkDef);
  const trackingPhone = usePropertyField('property_call_tracking_phone') as string | null;
  // Fallback chain: editor override / custom_ppc_tracking_phone_number → property_call_tracking_phone
  const phone = (callLink.phone && callLink.phone.trim()) ? callLink.phone : (trackingPhone || '');

  const phoneUrl = phone
    ? `tel:${phone.replace(/[^+\d]/g, '')}`
    : '#';

  return (
    <section className="relative bg-primary overflow-hidden">
      {/* Background image */}
      {heroImage.image && (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage.image})` }}
        />
      )}

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/65" />

      <div className="relative z-10 flex flex-col items-center text-center px-4 py-16 md:py-24">
        {/* Background image editable handle */}
        <EditableSlotGroup
          groupPath="thankyouhero.background"
          definition={heroImageDefinition}
          override={slotGroupOverrides['thankyouhero.background']}
          style={{ position: 'absolute', top: '1rem', left: '1rem', zIndex: 10 }}
        >
          <span className="hidden">Edit Background Image</span>
        </EditableSlotGroup>

        {/* Logo */}
        <EditableSlotGroup
          groupPath="thankyouhero.logo"
          definition={logoSlotDefinition}
          override={slotGroupOverrides['thankyouhero.logo']}
        >
          <div className="mb-6">
            {logo.image ? (
              <VibesitesImage
                url={logo.image}
                alt={logo.alt || 'Property Logo'}
                className="h-16 md:h-20 w-auto object-contain mx-auto"
              />
            ) : (
              <span className="text-2xl font-semibold text-white font-heading">
                {logo.alt || 'Property'}
              </span>
            )}
          </div>
        </EditableSlotGroup>

        {/* Heading */}
        <EditableToken
          path="thankyouhero.heading"
          type="text"
          label="Heading"
          as="h1"
          className="text-[clamp(2rem,5vw,3.5rem)] font-bold font-heading text-white mb-3 [text-shadow:0_2px_10px_rgba(0,0,0,0.3)]"
        >
          {heading}
        </EditableToken>

        {/* Subtitle */}
        <EditableToken
          path="thankyouhero.subtitle"
          type="text"
          label="Subtitle"
          as="p"
          className="text-[clamp(1rem,2.5vw,1.35rem)] font-medium font-body text-white/90 mb-10 md:mb-14 max-w-2xl [text-shadow:0_1px_5px_rgba(0,0,0,0.3)]"
        >
          {subtitle}
        </EditableToken>

        {/* Two CTA cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 w-full max-w-3xl">
          {/* Visit Website card */}
          <div className="flex flex-col items-center gap-4 p-8">
            <EditableToken
              path="thankyouhero.visit_description"
              type="text"
              label="Visit Website Description"
              as="p"
              className="text-white/90 font-body text-base md:text-lg leading-relaxed m-0"
            >
              {visitDescription}
            </EditableToken>

            <EditableSlotGroup
              groupPath="thankyouhero.visit_website_link"
              definition={visitWebsiteLinkDef}
              override={slotGroupOverrides['thankyouhero.visit_website_link']}
            >
              <a
                href={buildCtaUrl(visitLink.url, visitLink.query_params)}
                target={visitLink._target || '_blank'}
                rel="noopener noreferrer"
                className="inline-block bg-primary text-primary-foreground font-semibold text-sm md:text-base py-3 px-8 rounded no-underline transition-all duration-200 hover:opacity-90 hover:shadow-lg uppercase tracking-wide"
              >
                {visitLink.label || 'VISIT OUR WEBSITE'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Call Now card */}
          <div className="flex flex-col items-center gap-4 p-8">
            <EditableToken
              path="thankyouhero.call_description"
              type="text"
              label="Call Now Description"
              as="p"
              className="text-white/90 font-body text-base md:text-lg leading-relaxed m-0"
            >
              {callDescription}
            </EditableToken>

            <EditableSlotGroup
              groupPath="thankyouhero.call_now_link"
              definition={callNowLinkDef}
              override={slotGroupOverrides['thankyouhero.call_now_link']}
            >
              <a
                href={phoneUrl}
                target={callLink._target || '_self'}
                className="inline-block bg-primary text-primary-foreground font-semibold text-sm md:text-base py-3 px-8 rounded no-underline transition-all duration-200 hover:opacity-90 hover:shadow-lg uppercase tracking-wide"
              >
                {callLink.label || 'CALL NOW'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Content Schema
// ---------------------------------------------------------------------------

export const contentSchema: ComponentContentSchema = {
  slot: 'ThankYouHero',
  displayName: 'Thank You Hero',
  tokens: {
    'thankyouhero.heading': {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Thank You!',
    },
    'thankyouhero.subtitle': {
      label: 'Subtitle',
      type: 'text',
      defaultValue: 'A community leasing professional will contact you shortly.',
    },
    'thankyouhero.visit_description': {
      label: 'Visit Website Description',
      type: 'text',
      defaultValue: 'Visit our community website and learn more!',
    },
    'thankyouhero.call_description': {
      label: 'Call Now Description',
      type: 'text',
      defaultValue: 'Want to speak to a leasing professional now?',
    },
  },
  slotGroups: {
    'thankyouhero.logo': logoSlotDefinition,
    'thankyouhero.background': heroImageDefinition,
    'thankyouhero.visit_website_link': visitWebsiteLinkDef,
    'thankyouhero.call_now_link': callNowLinkDef,
  },
};

(ThankYouHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ThankYouHero;
